import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseMutationClearOptions } from 'shared/types/tanstack-utility-types'
import { commentsKeys } from 'modules/comments/api/key-factory'
import type { AddCommentRequestType } from 'modules/comments/api/types'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import type { CommentsTypeEnum } from 'modules/comments/types'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'

interface UseAddCommentOptions<T extends CommentsTypeEnum> extends Omit<
  UseMutationClearOptions<CommentInterface, Error, AddCommentRequestType<T>>,
  'gcTime'
> {
  commentsType: T
  pageId: number
}

export const useAddComment = <T extends CommentsTypeEnum>({
  commentsType,
  pageId,
  ...options
}: UseAddCommentOptions<T>): UseMutationResult<
  CommentInterface,
  Error,
  AddCommentRequestType<T>
> => {
  const queryClient = useQueryClient()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    CommentInterface,
    AddCommentRequestType<T>
  >({
    method: RequestMethodsEnum.post,
  })

  return useMutation({
    mutationKey: commentsKeys.add(commentsType, pageId),
    mutationFn: async ({ attachments, ...body }: AddCommentRequestType<T>) =>
      fetcher(COMMENT_API_PREFIX, {
        ...body,
        attachments: escapeAttachmentLocalFields(attachments),
      }),
    ...options,
    gcTime: Infinity,
    onSuccess: (...args) => {
      void queryClient.invalidateQueries(
        { queryKey: commentsKeys.resource(commentsType, pageId) },
        { cancelRefetch: false },
      )
      return options.onSuccess?.(...args)
    },
  })
}
